import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, InfoCircleIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useChangeStep } from '@/features/experience-shell/hooks/use-change-step.ts'
import {
	type Experience,
	StepKeyName
} from '@/features/experience-shell/types.ts'
import { Trans, useTranslation } from '@/translations/i18n'
import { checkIsItineraryValid } from '../utils'
import { ActivityCard } from './activity-card'
import { Content, Footer, SectionDescription } from './atoms'

export const ItineraryStep = ({
	experienceId,
	galleryId,
	itineraries,
	isUpgrade
}: {
	experienceId: number
	galleryId: number
	itineraries: Experience['itineraries']
	isUpgrade: boolean
}) => {
	const { t } = useTranslation()
	const [isErrorRendered, setIsErrorRendered] = useState(false)

	const { editStepMutation } = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.itinerary,
		isUpgrade
	})
	return (
		<>
			<Content gap={4} title={t('experience_shell:step_itinerary')}>
				<div className='flex flex-col gap-6'>
					<div className='flex flex-col gap-4'>
						<SectionDescription>
							{t('experience_shell:itinerary_description')}
						</SectionDescription>
						<div className='flex w-full items-center gap-3 rounded-xl border border-neutral-300 p-4 text-neutral-900 text-sm [&_u]:cursor-pointer [&_u]:underline'>
							<div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-500 text-neutral-white'>
								<InfoCircleIcon size={20} />
							</div>
							<p>
								<Trans
									components={{
										u: (
											<button
												onClick={() =>
													editStepMutation.mutate(
														StepKeyName.enum
															.merchandising
													)
												}
												style={{
													background: 'none',
													border: 'none',
													padding: 0,
													textDecoration: 'underline',
													color: 'inherit',
													font: 'inherit',
													cursor: 'pointer'
												}}
												type='button'
											/>
										)
									}}
									i18nKey='experience_shell:itinerary_banner_text'
								/>
							</p>
						</div>
					</div>
					<div className='font-medium text-lg text-neutral-900'>
						{t('experience_shell:x_activities', {
							count: itineraries.length
						})}
					</div>
					{itineraries.map((item) => (
						<Fragment key={item.id}>
							<ActivityCard
								experienceId={experienceId}
								galleryId={galleryId}
								itinerary={item}
							/>
							{isErrorRendered &&
								!checkIsItineraryValid(item) && (
									<InputHelpText
										error={t('errors:required')}
									/>
								)}
							<Separator orientation='horizontal' />
						</Fragment>
					))}
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={editStepMutation.isPending}
					onClick={() => {
						if (
							isUpgrade &&
							itineraries.some(
								(itinerary) => !checkIsItineraryValid(itinerary)
							)
						) {
							setIsErrorRendered(true)
							return
						}
						editStepMutation.mutate(StepKeyName.enum.photos)
					}}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{editStepMutation.isPending &&
						editStepMutation.variables ===
							StepKeyName.enum.photos && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={editStepMutation.isPending}
					onClick={() => {
						if (
							isUpgrade &&
							itineraries.some(
								(itinerary) => !checkIsItineraryValid(itinerary)
							)
						) {
							setIsErrorRendered(true)
							return
						}
						editStepMutation.mutate('pricing-discounts')
					}}
					type='button'
				>
					{t('common:continue')}
					{editStepMutation.isPending &&
					editStepMutation.variables === 'pricing-discounts' ? (
						<Spinner />
					) : (
						<ArrowRightIcon />
					)}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
